import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '../../store/AuthContext'
import scoresService from '../../services/scoresService'
import employeesService from '../../services/employeesService'
import { StatCard } from '../../components/StatCard'
import { RankingTable } from '../../components/RankingTable'
import { QuarterlyChart } from '../../components/QuarterlyChart'
import { getQuarterFromMonth } from '../../utils/formatters'
import { GroupHealthOverview } from '../../components/GroupHealthOverview'

export const DashboardPage = () => {
  const { user } = useAuth()

  // Current date
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentQuarter = getQuarterFromMonth(now.getMonth() + 1)

  // Fetch current user's employee data
  const { data: allEmployees = [], isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesService.getAll(),
  })

  const myEmployee = allEmployees.find((e) => e.user_detail?.id === user?.id)
  const hasEmployee = !isLoadingEmployees && !!myEmployee

  // Fetch quarterly summary
  const { data: quarterlySummary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ['quarterly-summary', currentYear, currentQuarter],
    queryFn: () => scoresService.getQuarterlySummary(currentYear, currentQuarter),
  })

  // Fetch quarterly chart (only if employee exists)
  const { data: quarterlyChart, isLoading: isLoadingChart } = useQuery({
    queryKey: ['quarterly-chart', myEmployee?.id, currentYear],
    queryFn: () => scoresService.getQuarterlyChart(myEmployee!.id, currentYear),
    enabled: !!myEmployee,
  })

  // Get ranking employees (backend guarantees array)
  const rankingEmployees = quarterlySummary?.employees || []

  // Find my data in summary
  const myData = rankingEmployees.find(
    (e) => e.employee_id === myEmployee?.id
  )

  // Calculate my ranking position
  const sortedEmployees = [...rankingEmployees].sort((a, b) => b.average - a.average)
  const myPosition = myData
    ? sortedEmployees.findIndex((e) => e.employee_id === myEmployee?.id) + 1
    : null

  // Top 5 for preview
  const top5Employees = sortedEmployees.slice(0, 5)

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
        Dashboard
      </h1>

      {/* Welcome Card */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Bem-vindo, {user?.first_name || user?.username}! 👋
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {myEmployee?.primary_company_detail?.name
            ? `${myEmployee.primary_company_detail.name} — Sistema de pontuação por atitudes`
            : 'Sistema de pontuação por atitudes — Jonnpo'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total de Pontos"
          value={myEmployee?.total_points ?? (hasEmployee ? 0 : '-')}
          icon="⭐"
          color="primary"
          subtitle={hasEmployee ? 'Acumulado' : 'Sem perfil vinculado'}
          isLoading={isLoadingEmployees}
        />

        <StatCard
          title="Média Trimestre"
          value={myData?.average ?? 0}
          icon="📊"
          color="success"
          subtitle={`Q${currentQuarter} ${currentYear}`}
          isLoading={isLoadingSummary}
        />

        <StatCard
          title="Posição Ranking"
          value={myPosition ? `${myPosition}º` : '-'}
          icon="🏆"
          color="warning"
          subtitle={rankingEmployees.length > 0 ? `de ${rankingEmployees.length}` : undefined}
          isLoading={isLoadingSummary}
        />

        <StatCard
          title="Funcionários"
          value={rankingEmployees.length}
          icon="👥"
          color="danger"
          subtitle="Elegíveis este trimestre"
          isLoading={isLoadingSummary}
        />
      </div>

      {/* Company Health — primary + group */}
      <GroupHealthOverview primaryCompanyId={myEmployee?.primary_company_detail?.id} />

      {/* Self-Assessment Status (Viewer) */}
      {user?.role === 'viewer' && myEmployee?.is_ownership_eligible && (
        <SelfAssessmentDashboardCard />
      )}

      {/* Pending Approvals (Admin) */}
      {user?.role === 'admin' && (
        <PendingApprovalsDashboardCard />
      )}

      {/* Chart */}
      {myEmployee && (
        <div className="mb-6">
          <QuarterlyChart
            data={quarterlyChart?.quarters ?? []}
            year={currentYear}
            isLoading={isLoadingChart}
          />
        </div>
      )}

      {/* Top 5 Ranking Preview */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Top 5 Ranking
          </h2>
          <Link
            to="/ranking"
            className="text-sm text-primary hover:text-primary-hover font-medium"
          >
            Ver ranking completo →
          </Link>
        </div>

        <RankingTable
          employees={top5Employees}
          isLoading={isLoadingSummary}
          highlightEmployeeId={myEmployee?.id}
        />
      </div>

      {/* Info Card - Only show if no employee linked after loading (not for admins) */}
      {!isLoadingEmployees && !myEmployee && user?.role !== 'admin' && (
        <div className="bg-warning/10 border border-warning/20 rounded-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            ℹ️ Perfil de Funcionário Não Encontrado
          </h3>
          <p className="text-gray-700 dark:text-gray-300">
            Seu usuário ainda não está vinculado a um perfil de funcionário.
            Entre em contato com o administrador do sistema.
          </p>
        </div>
      )}
    </div>
  )
}

const MONTH_NAMES_DASH = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const SelfAssessmentDashboardCard = () => {
  const now = new Date()
  const currentMonth = now.getMonth() + 1
  const currentDay = now.getDate()

  const { data: assessmentStatus } = useQuery({
    queryKey: ['my-assessment-status'],
    queryFn: () => scoresService.getMyAssessmentStatus(),
  })

  if (!assessmentStatus?.is_eligible || !assessmentStatus?.self_assessment_enabled) return null

  const isAdminCreated = assessmentStatus.is_admin_created
  const status = assessmentStatus.status
  const score = assessmentStatus.score

  // Admin created the assessment — show informational card (yellow if negative, blue otherwise)
  if (isAdminCreated && score) {
    const isNegative = score.points_snapshot <= 0
    return (
      <Link to="/minha-avaliacao" className="block mb-6">
        <div className={`border rounded-card p-6 hover:shadow-md transition-shadow ${
          isNegative
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
        }`}>
          <div className="flex items-center gap-3">
            <div className="text-2xl">{isNegative ? '⚠️' : '📋'}</div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {isNegative ? 'Avaliação Registrada — Atenção' : 'Avaliação Registrada'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                O administrador registrou sua avaliação de {MONTH_NAMES_DASH[currentMonth - 1]}. {score.points_snapshot > 0 ? '+' : ''}{score.points_snapshot} pontos.
                {isNegative ? ' Busque melhorias!' : ''}
              </p>
            </div>
          </div>
        </div>
      </Link>
    )
  }

  // No self-assessment yet
  if (!status) {
    // Show CTA for current month from day 28 onwards
    if (currentDay >= 28) {
      return (
        <Link to="/minha-avaliacao" className="block mb-6">
          <div className="bg-primary/10 border border-primary/20 rounded-card p-6 hover:bg-primary/15 transition-colors">
            <div className="flex items-center gap-3">
              <div className="text-2xl">📝</div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Fazer Autoavaliação</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Envie sua autoavaliação de {MONTH_NAMES_DASH[currentMonth - 1]}.
                </p>
              </div>
            </div>
          </div>
        </Link>
      )
    }

    // Before that: suggest checking previous months (only if not January)
    if (currentMonth > 1) {
      return (
        <Link to="/minha-avaliacao" className="block mb-6">
          <div className="bg-primary/10 border border-primary/20 rounded-card p-6 hover:bg-primary/15 transition-colors">
            <div className="flex items-center gap-3">
              <div className="text-2xl">📝</div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Minha Autoavaliação</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Verifique se há meses anteriores pendentes de autoavaliação.
                </p>
              </div>
            </div>
          </div>
        </Link>
      )
    }

    // January, not near end of month — no card
    return null
  }

  // Approved: yellow if negative/zero points OR approved with changes (ressalvas)
  const needsAttention = score && (score.points_snapshot <= 0 || (score.original_attitudes && score.original_attitudes.length > 0))

  const statusConfig = {
    pending: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700',
      icon: '⏳',
      title: 'Aguardando Aprovação',
      text: `Sua autoavaliação de ${MONTH_NAMES_DASH[currentMonth - 1]} está pendente.`,
    },
    rejected: {
      bg: 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700',
      icon: '✗',
      title: 'Avaliação Rejeitada',
      text: 'Sua autoavaliação foi rejeitada. Revise e reenvie.',
    },
    approved: needsAttention
      ? {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700',
          icon: '⚠️',
          title: 'Avaliação Aprovada — Atenção',
          text: score!.points_snapshot <= 0
            ? `Sua avaliação de ${MONTH_NAMES_DASH[currentMonth - 1]} resultou em ${score!.points_snapshot} pontos. Busque melhorias!`
            : `Sua avaliação de ${MONTH_NAMES_DASH[currentMonth - 1]} foi aprovada com ressalvas. ${score!.points_snapshot > 0 ? '+' : ''}${score!.points_snapshot} pontos.`,
        }
      : {
          bg: 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700',
          icon: '✓',
          title: 'Autoavaliação Aprovada',
          text: `Sua autoavaliação de ${MONTH_NAMES_DASH[currentMonth - 1]} foi aprovada! +${score?.points_snapshot ?? 0} pontos.`,
        },
  }

  const config = statusConfig[status]
  return (
    <Link to="/minha-avaliacao" className="block mb-6">
      <div className={`border rounded-card p-6 hover:shadow-md transition-shadow ${config.bg}`}>
        <div className="flex items-center gap-3">
          <div className="text-2xl">{config.icon}</div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{config.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{config.text}</p>
          </div>
        </div>
      </div>
    </Link>
  )
}

const PendingApprovalsDashboardCard = () => {
  const { data: pendingCount } = useQuery({
    queryKey: ['pending-count'],
    queryFn: () => scoresService.getPendingCount(),
    refetchInterval: 60000,
  })

  if (!pendingCount?.count) return null

  return (
    <Link to="/aprovacoes" className="block mb-6">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-card p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3">
          <div className="text-2xl">📥</div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Avaliações Pendentes
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {pendingCount.count} autoavaliação(ões) aguardando sua revisão.
            </p>
          </div>
          <span className="min-w-[28px] h-[28px] flex items-center justify-center bg-danger text-white text-sm font-bold rounded-full px-2">
            {pendingCount.count}
          </span>
        </div>
      </div>
    </Link>
  )
}

export default DashboardPage
